<template>
  <div class="c-participate__container">
    <div v-if="isLoading" class="c-loading__container u-mbm">
      <SVGIcon class="loading-icon" width="80" height="80" name="loading" />
    </div>
    <template v-else-if="participatePageData">
      <div class="c-participate__header">
        <h1 class="c-participate__title">{{ getParticipateDataLocale('title') }}</h1>
        <!-- safe to use here as it's a static string -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="showPageHeader" class="c-participate__content" v-html="getParticipateDataLocale('content')" />
      </div>
      <div class="c-participate__actions">
        <div class="c-gallery">
          <ul class="c-gallery__items">
            <li v-for="(action, index) in participateActions" :key="index" class="c-gallery__item">
              <div
                :class="setCardBackground(action.name_en, index, hasImage(action.thumbnail_368_256_crop), action.hex_color)"
                class="c-card c-card__participate"
              >
                <img
                  v-if="action.thumbnail_368_256_crop && action.thumbnail_368_256_crop.length"
                  class="c-card__picture"
                  :src="hasImage(action.thumbnail_368_256_crop) ? action.thumbnail_368_256_crop : baseUrl + 'img/participate-project.png'"
                  alt="card thumbnail"
                >
                <div v-if="action.thumbnail_368_256_crop && action.thumbnail_368_256_crop.length" class="c-card__background-overlay" />
                <div class="c-card__header">
                  <h2 class="c-card__title">
                    <a :href="action.url ? action.url : '#'" class="c-card__link">{{ getActionDataLocale(action, 'name') }}</a>
                  </h2>
                </div>
                <div class="c-card__content">
                  <p class="c-card__text">{{ getActionDataLocale(action, 'description') }}</p>
                  <a class="c-participate__link" :href="action.url ? action.url : '#'">
                    <SVGIcon class="c-participate__link-icon c-button__icon" name="arrow-right" />
                    <p class="c-participate__link-label">{{ getActionDataLocale(action, 'action') }}</p>
                  </a>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { Page, PageAction, getPages } from '@/utils/api'
import { marked } from 'marked'
import { urlExists } from '@/utils/urls'
import SVGIcon from '@/components/SVGIcon.vue'
// import { useI18n } from '@/utils/i18n'

const props = defineProps<{
  showHeader?: string
  showFeatured?: string
}>()

const isLoading = ref(true)
const isHexColorParsed = ref(false)

const baseUrl = '/static/'

const pageLocale = ref('')

const allPages = ref<Page[]>([])
const participatePageData = ref<Page>()
const participateActions = ref<PageAction[]>()

const showPageHeader = ref(false)
const showFeaturedOnly = ref(false)

const cardColors = ref([ 'red', 'green', 'blue', 'orange', 'purple' ])

function hasImage (imageUrl: string): boolean {
  if (imageUrl) {
    return (imageUrl.length > 0) && urlExists(imageUrl)
  } else {
    return false
  }
}

function setCardBackground (actionName: string, index: number, hasImage: boolean, hexColor: string): string {
  let cardClass = ''

  if (actionName.length) {
    const cardName = actionName.toLocaleLowerCase()

    if (cardName.includes('project')) {
      cardClass = 'c-participate__project '
    } else if (cardName.includes('download')) {
      cardClass = 'c-participate__download '
    } else if (cardName.includes('discuss')) {
      cardClass = 'c-participate__discuss '
    } else if (cardName.includes('workshop')) {
      cardClass = 'c-participate__workshop '
    }

    if (!hasImage) {
      if (hexColor) {
        cardClass += setCardColorHexClass(hexColor)
      } else {
        cardClass += setCardColorClass(index)
      }
    } else {
      if (hexColor) {
        cardClass += 'c-participate__has-image' + ' ' + setCardColorHexClass(hexColor)
      } else {
        cardClass += 'c-participate__has-image' + ' ' + setCardColorClass(index)
      }
    }

    if ((index + 1) === participateActions.value?.length) {
      isHexColorParsed.value = true
    }
  }

  return cardClass
}

function setCardBackgroundHex () {
  const cardsElements = document.querySelectorAll('.c-card__participate-hex')
  for (let i = 0; i < cardsElements.length; i++) {
    const el = cardsElements[i] as HTMLElement
    const colorCode = Array.from(el.classList).filter(c => c.startsWith('hex'))[0].split('-')[2]
    el.style.backgroundColor = '#' + colorCode
    const elOverlay = el.getElementsByClassName('c-card__background-overlay')[0] as HTMLElement
    if (elOverlay) {
      elOverlay.style.backgroundColor = '#' + colorCode
    }
  }
}

function setCardColorClass (index: number): string {
  const color = ref(cardColors.value[index % cardColors.value.length])
  return 'c-card__participate-' + color.value
}

function setCardColorHexClass (hexColor: string): string {
  return 'c-card__participate-hex' + ' ' + 'hex-color-' + hexColor
}

async function getParticipatePage (): Promise<any> {
  allPages.value = await getPages()

  if (allPages.value && allPages.value.length) {
    for (let i = 0; i < allPages.value.length; i++) {
      if (allPages.value[i].title.toLowerCase() === 'participate' || allPages.value[i].title.toLowerCase() === 'participer') {
        return allPages.value[i]
      }
    }
  }
}

function getParticipateDataLocale (data: string): string {
  if (participatePageData.value) {
    if (pageLocale.value === 'en') {
      // EN
      switch (data) {
        case 'title':
          return participatePageData.value.title_en
        case 'description':
          return participatePageData.value.description_en
        case 'content':
          return marked.parseInline(participatePageData.value.content_en)
        case 'action_name':
          return participatePageData.value.title_en
        default:
          return ''
      }
    } else if (pageLocale.value === 'fr') {
      // FR
      switch (data) {
        case 'title':
          return participatePageData.value.title_fr
        case 'description':
          return participatePageData.value.description_fr
        case 'content':
          return marked.parseInline(participatePageData.value.content_fr)
        case 'action_name':
          return participatePageData.value.title_fr
        default:
          return ''
      }
    } else {
      return ''
    }
  } else {
    return ''
  }
}

function getActionDataLocale (action: PageAction, data: string): string {
  if (action) {
    if (pageLocale.value === 'en') {
      // EN
      switch (data) {
        case 'name':
          return action.name_en
        case 'description':
          return action.description_en
        case 'action':
          return action.action_en
        default:
          return ''
      }
    } else if (pageLocale.value === 'fr') {
      // FR
      switch (data) {
        case 'name':
          return action.name_fr
        case 'description':
          return action.description_fr
        case 'action':
          return action.action_fr
        default:
          return ''
      }
    } else {
      return ''
    }
  } else {
    return ''
  }
}

watch([ isHexColorParsed ], async () => {
  if (isHexColorParsed.value) {
    setCardBackgroundHex()
  }
}, { immediate: true })

onMounted(async () => {
  pageLocale.value = document.documentElement.lang

  if (props.showHeader === 'True') {
    showPageHeader.value = true
  } else {
    showPageHeader.value = false
  }

  if (props.showFeatured === 'True') {
    showFeaturedOnly.value = true
  } else {
    showFeaturedOnly.value = false
  }

  participatePageData.value = await getParticipatePage()
  console.log('PARTICIPATE PAGE DATA: ', participatePageData.value)

  if (participatePageData.value) {
    const featuredActions = ref(participatePageData.value.actions.filter((action: PageAction) => action.is_featured === true))

    if (showFeaturedOnly.value && (featuredActions.value.length > 0)) {
      participateActions.value = featuredActions.value
    } else {
      participateActions.value = participatePageData.value.actions
    }
    console.log('PARTICIPATE PAGE ACTIONS: ', participateActions.value)
  }

  isLoading.value = false
})

</script>

<!-- <style scoped lang="scss">

</style> -->
